import React, { useEffect, useState } from 'react';
import '../App.css';
import '../assets/RouteModal.css';
import { routeApi, driversApi, ApiError } from '../utils/api';

interface BlockedOrder {
  order_number: string;
  customer_name?: string;
  reasons: string[];
}

interface RouteModalProps {
  selectedOrders: string[];
  onClose: () => void;
  onAssignSuccess?: () => void;
}

export const RouteModal: React.FC<RouteModalProps> = ({ onClose, selectedOrders, onAssignSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<BlockedOrder[]>([]);
  const [result, setResult] = useState<any | null>(null);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [checking, setChecking] = useState(true);

  /* The client can only see the customer's coordinates. A shop whose name
     matches no shop row is just as unroutable, and only the backend knows —
     so ask before the user hits Optimize rather than after. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const check = await routeApi.validate(selectedOrders);
        if (!cancelled) setBlocked(check.blocked_orders ?? []);
      } catch {
        /* Pre-flight is an optimization; optimize still reports the truth. */
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedOrders]);

  const submit = async () => {
    setError(null);
    setBlocked([]);
    setResult(null);
    setLoading(true);
    try {
      const response = await routeApi.optimiseByOrderNumber({
        order_ids: selectedOrders,
        driver_start: '11:00',
        driver_end: '19:00',
        time_limit_s: 10,
        traffic_model: 'best_guess',
      });
      setResult(response);
    } catch (e: any) {
      // A 422 carries per-order reasons; show those rather than one flat string.
      if (e instanceof ApiError && Array.isArray(e.payload?.blocked_orders)) {
        setBlocked(e.payload.blocked_orders);
      }
      setError(e.message || 'Route optimization failed');
    } finally {
      setLoading(false);
    }
  };

  const openDrivers = async () => {
    setDriversLoading(true);
    setShowDriverModal(true);
    try {
      setDrivers(await driversApi.getAll());
    } catch {
      setDrivers([]);
    } finally {
      setDriversLoading(false);
    }
  };

  const whatsappText = () => {
    if (!result?.route) return '';
    const stops = result.route
      .map((r: any) => `${r.sequence}. ${r.name} [${r.type}] ETA ${r.eta_hhmm}`)
      .join('\n');
    return encodeURIComponent(`Optimized route:\n${stops}\n\nGoogle Maps: ${result.maps_url}`);
  };

  const sendToDriver = async (phone: string, driverId: string) => {
    const sanitized = phone.replace(/[^\d]/g, '').replace(/^0+/, '');
    // Open the tab synchronously — a popup opened after an await is blocked.
    const win = window.open(`https://wa.me/${sanitized}?text=${whatsappText()}`, '_blank');
    setAssigning(true);
    try {
      await Promise.all(selectedOrders.map(n => driversApi.assign_driver(driverId, n)));
      setShowDriverModal(false);
      onAssignSuccess?.();
      onClose();
    } catch (e: any) {
      setError(`Route sent, but assigning the driver failed: ${e.message ?? e}`);
      setShowDriverModal(false);
    } finally {
      setAssigning(false);
      if (!win) {
        setError('Allow pop-ups for this site to open WhatsApp.');
      }
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="rmodal" onClick={e => e.stopPropagation()} role="dialog" aria-label="Route optimizer">
        <header className="rmodal__head">
          <div>
            <h2 className="rmodal__title">Plan route</h2>
            <p className="rmodal__sub">
              {selectedOrders.length} order{selectedOrders.length === 1 ? '' : 's'} selected
            </p>
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </header>

        {blocked.length > 0 && (
          <div className="banner banner--warn rmodal__blocked">
            <span>
              <span className="banner__title">These orders can’t be routed</span>
              <ul className="rmodal__blocked-list">
                {blocked.map(b => (
                  <li key={b.order_number}>
                    <span className="mono">{b.order_number}</span>
                    {b.customer_name ? ` · ${b.customer_name}` : ''} — {b.reasons.join('; ')}
                  </li>
                ))}
              </ul>
              Remove them from the selection to continue.
            </span>
          </div>
        )}

        {error && blocked.length === 0 && (
          <div className="banner banner--danger">{error}</div>
        )}

        {!result && (
          <button
            className="btn btn--primary btn--block"
            disabled={loading || checking || blocked.length > 0}
            onClick={submit}
          >
            {checking ? 'Checking orders…' : loading ? 'Optimizing…' : 'Optimize route'}
          </button>
        )}

        {result && (
          <div className="rmodal__result">
            <div className="rmodal__stops">
              {result.route.map((r: any) => (
                <div className="rstop" key={r.sequence}>
                  <span className="rstop__seq">{r.sequence}</span>
                  <div className="rstop__body">
                    <p className="rstop__name">{r.name}</p>
                    <p className="rstop__type">{r.type}</p>
                  </div>
                  <span className="rstop__eta mono">{r.eta_hhmm}</span>
                </div>
              ))}
            </div>
            <div className="rmodal__foot">
              <a href={result.maps_url} target="_blank" rel="noopener noreferrer"
                 className="btn btn--secondary">Open in Maps</a>
              <button className="btn btn--primary" onClick={openDrivers} disabled={assigning}>
                {assigning ? 'Assigning…' : 'Send to driver'}
              </button>
            </div>
          </div>
        )}
      </div>

      {showDriverModal && (
        <div className="modal-overlay" onClick={() => setShowDriverModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3 className="rmodal__title">Send to driver</h3>
            <p className="rmodal__sub">Assigns all {selectedOrders.length} orders and opens WhatsApp.</p>
            {driversLoading ? (
              <div className="state"><p className="state__body">Loading drivers…</p></div>
            ) : drivers.length === 0 ? (
              <div className="state"><p className="state__body">No drivers yet. Add one on the Drivers tab.</p></div>
            ) : (
              <div className="rmodal__drivers">
                {drivers.map(d => (
                  <button key={d.id ?? d.phone} className="rdriver" disabled={assigning}
                          onClick={() => sendToDriver(d.phone, d.id)}>
                    <span className="rdriver__name">{d.name}</span>
                    <span className="rdriver__phone mono">{d.phone}</span>
                  </button>
                ))}
              </div>
            )}
            <button className="btn btn--secondary btn--block" onClick={() => setShowDriverModal(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
